import { Dispatch } from 'redux';
import apis from '../../utils/apis';
import { handleApiError } from './utils/utils';
import { DropdownOption } from '../../components/shared/TailwindComponents/Dropdown';

const SET_DROPDOWN_DATA = 'SET_DROPDOWN_DATA';

const setDropdownData = (dropdownKey: string, data: DropdownOption[]) => ({
  type: SET_DROPDOWN_DATA,
  payload: { dropdownKey, data }
});

const fetchDropdownData = (
  endpoint: string,
  dropdownKey: string,
  params?: any,
  /** Per-endpoint mapping, for payloads the generic shape-sniffing gets wrong. */
  mapItem?: (item: any) => DropdownOption
) => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await apis.get(endpoint, { params });
      const dataArray = Array.isArray(response.data.list) ? response.data.list : Array.isArray(response.data.data) ? response.data.data : null;
      if (response && response.status === 200 && dataArray) {
        const dataList: DropdownOption[] = dataArray.map((item: any) => {
          if (mapItem) return mapItem(item);
          let value = '';
          let label = '';

          if ('MatterTypeID' in item && 'Code' in item) {
            value = item.Code;
            label = item.Label;
          } else if ('MatterStatusID' in item && 'Code' in item) {
            value = item.Code;
            label = item.Label;
          } else if ('StageName' in item) {
            value = item.StageName;
            label = item.StageName;
            return { value, label, description: item.StageDescription };
          } else if ('DepartmentID' in item && 'DepartmentName' in item) {
            value = item.DepartmentID.toString();
            label = item.DepartmentName;
          } else if ('role_id' in item && 'role_name' in item) {
            value = item.role_id.toString();
            label = item.role_name;
          } else if ('PriorityID' in item && 'PriorityName' in item) {
            value = item.PriorityID;
            label = item.PriorityName;
          } else if ('CategoryID' in item && 'CategoryName' in item) {
            value = item.CategoryID;
            label = item.CategoryName;
          } else if ('StatusID' in item && 'StatusName' in item) {
            value = item.StatusID;
            label = item.StatusName;
          } else if ('Value' in item && 'DisplayName' in item) {
            value = item.Value;
            label = item.DisplayName || '';
          }else if ("MatterRoleID" in item){
            value = item.MatterRoleID;
            label = item.Label

          }else if ("CollaboratorRoleID" in item){
            value = item.Label;
            label = item.Label
          }
           else if ('LegalSubjectID' in item) {
            return {
              value: item.LegalSubjectID.toString(),
              label: item.SubCategory,
              category: item.Category,
              areas: item.Areas,
            };
          } else if ('id' in item && 'name' in item) {
            value = item.id?.toString() || item.ID || item.Value || '';
            label = item.name || item.DisplayName || item.Name || '';
            return {
              value,
              label,
              active: item.active !== undefined ? item.active : undefined,
              ...(item.description && { description: item.description })
            };
          } else {
            value = item.ID || item.Value || '';
            label = item.DisplayName || item.Name || '';
          }

          return { value, label, active: item.active !== undefined ? item.active : undefined };
        });
        dispatch(setDropdownData(dropdownKey, dataList));
      } else {
        handleApiError(response, dispatch);
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
    }
  };
};

export const fetchAIVoices = (language?: string) =>
  fetchDropdownData(
    language ? `/dropdown/ai-voices?language=${language}` : `/dropdown/ai-voices`,
    'aiVoices'
  );

/**
 * AI / output languages — the 55-language list from SQL.
 *
 * The route is `/dropdown/ai-languages` (`domains/dropdowns/routes.py:184`);
 * `/dropdown/languages` was never registered and 404'd on every call.
 *
 * Response: `{ data: [{ id, name, code, sort_order }] }`. The mapping is
 * explicit because the generic shape-sniffer would match on `id`+`name` and
 * use the numeric row id as the value — the platform's language convention is
 * the CODE (what `ai_settings.language` stores and what the research
 * `output_language` override is compared against).
 */
export const fetchAILanguages = () =>
  fetchDropdownData('/dropdown/ai-languages', 'aiLanguages', undefined, (item: any) => ({
    value: String(item.code ?? item.Code ?? ''),
    label: String(item.name ?? item.Name ?? item.code ?? ''),
  }));

export const fetchPlatformCurrencies = () => fetchDropdownData('/dropdown/platform-currencies', 'platformCurrencies');

export const fetchPlatformLanguages = () => fetchDropdownData('/dropdown/platform-languages', 'platformLanguages');

export const fetchPracticeAreas = () => fetchDropdownData('/dropdown/practice-areas', 'practiceAreas');

export const fetchDepartments = () => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await apis.get('/org-people/departments');

      if (response && response.status === 200 && Array.isArray(response.data.departments)) {
        const dataList: DropdownOption[] = response.data.departments.map((item: any) => ({
          value: item.id?.toString() || '',
          label: item.name || '',
          active: item.is_active !== undefined ? item.is_active : undefined,
          ...(item.description && { description: item.description })
        }));

        dispatch(setDropdownData('departments', dataList));
        return dataList;
      } else {
        handleApiError(response, dispatch);
        return [];
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return [];
    }
  };
};


export const fetchMatterTypes = (matterTypeId?: string) =>
  fetchDropdownData(
    matterTypeId ? `/dropdown/get_matter_types?type=${matterTypeId}` : '/dropdown/get_matter_types',
    'matterTypes'
  );

export const fetchMatterStatuses = () => fetchDropdownData('/dropdown/get_matter_status', 'matterStatuses');

export const fetchMatterClientRoles = () => fetchDropdownData('/dropdown/get_matter_client_roles', 'matterClientRoles' );

export const fetchMatterCollaboratorRoles = () => fetchDropdownData('/dropdown/get_matter_collaborator_roles', 'collaboratorRoles' );

export const fetchJurisdictions = () => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await apis.get('/dropdown/jurisdictions');

      if (response && response.status === 200 && Array.isArray(response.data.data)) {
        // Filter only active jurisdictions and transform to DropdownOption format
        const dataList: DropdownOption[] = response.data.data
          .filter((item: any) => item.is_active === true)
          .sort((a: any, b: any) => {
            // Sort by sort_order if available
            if (a.sort_order !== undefined && b.sort_order !== undefined) {
              return a.sort_order - b.sort_order;
            }
            return 0;
          })
          .map((item: any) => ({
            value: item.code || item.id?.toString() || '',
            label: item.name || item.label || '',
            active: item.is_active,
          }));

        dispatch(setDropdownData('aiJurisdictions', dataList));
        return dataList;
      } else {
        handleApiError(response, dispatch);
        return [];
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      return [];
    }
  };
};
